import { useEffect, useState } from 'react';
import { MessageSquareHeart, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CLS = {
  pending: 'bg-yellow-500/10 text-yellow-400',
  approved: 'bg-green-500/10 text-green-400',
  hidden: 'bg-[rgba(255,255,255,0.06)] text-[#A3A19C]',
};

export const ReviewsPanel = ({ token }) => {
  const { t, fmtDate } = useLang();
  const [text, setText] = useState('');
  const [mine, setMine] = useState([]);
  const [sending, setSending] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/reviews/mine`, { headers }).then(r => setMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) { toast.error(t('rev.min')); return; }
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { text: text.trim() }, { headers });
      toast.success(t('rev.sent'));
      setText('');
      load();
    } catch { toast.error(t('rev.fail')); }
    setSending(false);
  };

  return (
    <div className="bg-[#121215] border border-[rgba(212,175,55,0.15)] rounded-lg p-6 mb-10" data-testid="reviews-panel">
      <div className="flex items-start gap-3 mb-5">
        <MessageSquareHeart className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={1.5} />
        <div>
          <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{t('rev.title')}</h3>
          <p className="text-xs text-[#A3A19C]">{t('rev.sub')}</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <textarea rows={3} value={text} onChange={e => setText(e.target.value)} dir="auto" maxLength={1000} data-testid="review-text"
          className="w-full bg-[#08080A] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300 resize-none"
          placeholder={t('rev.ph')} />
        <button type="submit" disabled={sending} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="review-submit">
          <Send className="w-4 h-4" strokeWidth={1.5} /> {sending ? t('rev.sending') : t('rev.submit')}
        </button>
      </form>
      {mine.length > 0 && (
        <div className="mt-6 pt-5 border-t border-[rgba(255,255,255,0.06)] space-y-3" data-testid="my-reviews">
          <p className="text-xs font-semibold text-[#D4AF37]">{t('rev.mine')}</p>
          {mine.map(r => (
            <div key={r.id} className="bg-[#08080A] rounded-lg px-4 py-3 flex items-start justify-between gap-4" data-testid={`my-review-${r.id}`}>
              <p className="text-sm text-[#F5F2EB] leading-relaxed" dir="auto">{r.text}</p>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold ${STATUS_CLS[r.status]}`}>{t(`rev.status_${r.status}`)}</span>
                <span className="text-[10px] text-[#8C6C73]">{fmtDate(r.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
